import React, { useRef, useEffect, useState } from "react";
import CreateManualOrderForm, { CreateManualOrderFormInstance } from "./CreateManualOrderForm";
import { useRequest } from "ahooks";
import PaymentType from "@/enum/PaymentType";
import { batchManualAddOrder } from "@/services/api/manualOrder";
import { Button, message, Modal } from "antd";
import OrderDTO from "@/dto/order/OrderDTO";
import {
    getConsigneeMobile,
    getConsigneeTelephone,
    getConsigneeName,
    getConsigneeStreet,
    getOriginalIds,
    selectAllAvailableOrderItem,
} from "@/utils/OrderUtils";
import useAuthorityCheck from "@/hooks/useAuthorityCheck";
import Authority from "@/enum/Authority";
import { useGlobalStore } from "@/store/hooks";
import ShopType from "@/enum/ShopType";
import { uuid } from "@/utils";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import useSingleOrderDecryptedConsignee from "@/scenes/OrderManagement/hooks/useSingleOrderDecryptedConsignee";
import { getCurrentIsCanDecryptedShopType } from "@/utils/ShopUtils";
import { observer } from "mobx-react-lite";
import { v1VirtualMobileRegExp } from "@/utils/RegExp";
import Big from "big.js";
import { ManualOrderItem } from "@/scenes/manualOrder/Add/components/OrderItemForm/type";
import { ConsigneeInfoFormValues } from "@/scenes/manualOrder/Add/components/ConsigneeInfoForm/type";
import { isPaymentsLessZero, showPaymentsLessZeroModal } from "@/scenes/manualOrder/Add/util";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import PrintWaybillModal from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/PrintWaybillModal";
import { useModalStore } from "@/hooks/Modal/hooks";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

function getName(shopType: ShopType, order: OrderDTO) {
    if (shopType === ShopType.TAOBAO || shopType === ShopType.ALIBABA || shopType === ShopType.TAOBAO_DISTRIBUTION) {
        return order.buyerUsername;
    }

    return order.buyerNickname ?? order.buyerUsername;
}

interface CreateDecryptedManualOrderModalProps extends InjectBaseModalProps {
    order: OrderDTO;
    eventContext?: string;
}

const CreateDecryptedManualOrderModal: React.FC<CreateDecryptedManualOrderModalProps> = function (props) {
    const { order, open, onCancel, eventContext } = props;

    const form = useRef<CreateManualOrderFormInstance>(undefined);

    const { runAsync, loading } = useRequest(batchManualAddOrder, { manual: true });

    const { hasAuthority, openAuthorityModal } = useAuthorityCheck(Authority.ROLE_ORDER_IMPORT);
    const [privacyProtection, setPrivacyProtection] = useState(false);
    const { shopMapping } = useGlobalStore();
    const shop = shopMapping.get(order?.shopId);
    const modalStore = useModalStore();

    const { loading: decrypting, runAsync: handleSingleOrderDecryptedConsignee } = useSingleOrderDecryptedConsignee();

    async function handleSaveOrder(saveAndPrint = false) {
        const { consignee, orderItems, shopId, shippingAddress } = await form.current.getFormValues();

        // 代收货款
        let paymentType = PaymentType.OFFLINE;

        if (consignee.isCodCheckbox && consignee.codAmounts) {
            paymentType = PaymentType.COD;
        }

        if (paymentType === PaymentType.OFFLINE) {
            const { isLess, orderItemPrice, shippingCost, discounts, payments } = isPaymentsLessZero(
                orderItems,
                consignee.shippingCost,
                consignee.discounts,
            );

            if (isLess) {
                showPaymentsLessZeroModal(orderItemPrice, shippingCost, discounts, payments);

                return null;
            }
        }

        const splitMobile = consignee.mobile?.split("-");

        /** 手工录入订单的虚拟号：保存的时候需要转换格式保存，按这种格式保存：姓名(xxxx)，详细地址(分机号：xxxx) */
        const isVirtual = consignee.mobile && v1VirtualMobileRegExp.test(consignee.mobile);
        const virtualNo = isVirtual ? splitMobile?.[1] : "";
        const mergeConsigneeName = isVirtual ? consignee.consigneeName + `[${virtualNo}]` : consignee.consigneeName;
        const mergeConsigneeStreet = isVirtual ? consignee.street + `[${virtualNo}]` : consignee.street;
        const mergeConsigneeMobile = isVirtual ? splitMobile?.[0] : consignee.mobile;
        const { shipperName, id: shippingAddressId } = shippingAddress || {};

        const orderDto = {
            ...consignee,
            consigneeName: mergeConsigneeName,
            street: mergeConsigneeStreet,
            mobile: mergeConsigneeMobile,
            paymentType,
            orderItems,
            shipperName,
            shippingAddressId,
        };

        const results = await runAsync({ shopId, orders: [orderDto], saveAndPrint });
        const result = results?.[0];

        onCancel();

        collectEvent(DataTrackEvent.CREATE_PLATFORM_MANUAL_ORDER, {
            shop_id: shop.id,
            shop_name: shop.name,
            shop_type: ShopTypeNameMap[shop.type],
            type: "明文",
            context: eventContext,
        });

        if (result?.success && result?.order) {
            message.success("添加成功");

            return result.order;
        } else {
            Modal.error({ title: "保存失败，请联系客服反馈", content: result?.message });

            return null;
        }
    }

    async function injectOrderInfoToForm() {
        if (!hasAuthority) {
            openAuthorityModal();

            return;
        }

        if (getCurrentIsCanDecryptedShopType(shop.type)) {
            const listDecryptedConsigneeResults = await handleSingleOrderDecryptedConsignee(order);

            const result = listDecryptedConsigneeResults?.[0] || order;

            setPrivacyProtection(!!result.consignee.privacyProtection);

            const getOriginalId = () => {
                /**
                 * 拼多多取号最长订单编号是32位，原拼多多单号长度为22位，这里使用时间戳截断一下，筹够32位
                 * 同一个订单，多次创建明文订单，极端情况下可能会重复
                 */
                if ([ShopType.PINDUODUO, ShopType.PDD_FDS].includes(shop.type)) {
                    return order.originalId + "-" + Date.now().toString().slice(-9).padStart(9, "0");
                }

                return order.originalId + "-" + new Date().getTime();
            };

            const consigneeInfoFormValues: Partial<ConsigneeInfoFormValues> = {
                consigneeName: getConsigneeName(result),
                mobile: getConsigneeMobile(result),
                telephone: getConsigneeTelephone(result),
                provinceCode: order.provinceCode,
                cityCode: order.cityCode,
                districtCode: order.districtCode,
                street: getConsigneeStreet(result),
                discounts: order.discounts,
                shippingCost: order.shippingFee || 0,
                weight: Big(order.weight || 0)
                    .div(1000)
                    .toNumber(),
                buyerUsername: getName(shop.type, order),
                sellerRemarks:
                    (order.sellerRemarks?.localContent ?? order.sellerRemarks?.content ?? "") +
                    `原订单编号:${getOriginalIds(order).join()}`,
                originalId: getOriginalId(),
                buyerRemarks: order.buyerRemarks?.localContent ?? order.buyerRemarks?.content ?? "",
            };

            form.current.setConsigneeInfoFormValues(consigneeInfoFormValues);

            const manualOrderItems: ManualOrderItem[] = [];

            order.orderItems.forEach((orderItem) => {
                const product: ManualOrderItem = {
                    key: uuid(),
                    productOriginalId: orderItem.productOriginalId,
                    skuOriginalId: orderItem.skuOriginalId,
                };

                product.title = orderItem.title;
                product.price = orderItem.price;
                product.quantity = orderItem.quantity;
                product.skuExternalId = orderItem.skuExternalId;
                product.productExternalId = orderItem.productExternalId;
                product.skuProperties = orderItem.skuProperties;
                product.picture = orderItem.picture;
                product.simpleTitle = orderItem.simpleTitle;

                manualOrderItems.push(product);
            });

            form.current.setOrderItemsValues(manualOrderItems);

            form.current.setShipperAddressId(order.sellerRemarks?.shippingAddressId);
        }
    }

    async function handleSaveOrderAndPrint() {
        const manualOrder = await handleSaveOrder(true);

        if (manualOrder) {
            selectAllAvailableOrderItem(manualOrder);

            modalStore.openModal(PrintWaybillModal, {
                orders: [manualOrder],
                onShipTaskDone: () => {},
                defaultTemplateId: null,
                onUpdateOrder: () => {},
                onRefreshOrders: () => {},
                onUpdateSupplierOrderRemarks: () => {},
            });
        }
    }

    useEffect(() => {
        injectOrderInfoToForm();
    }, []);

    return (
        <Modal
            width={1500}
            title="创建手工/售后订单"
            open={open}
            onCancel={onCancel}
            footer={
                <>
                    <Button onClick={onCancel}>取消</Button>

                    <AutoLoadingButton
                        className="ant-btn-warning"
                        loading={loading || decrypting}
                        onClick={handleSaveOrderAndPrint}
                    >
                        保存并打印
                    </AutoLoadingButton>

                    <AutoLoadingButton
                        type="primary"
                        loading={loading || decrypting}
                        onClick={async () => {
                            await handleSaveOrder();
                        }}
                    >
                        保存订单
                    </AutoLoadingButton>
                </>
            }
        >
            <CreateManualOrderForm ref={form} privacyProtection={privacyProtection} />
        </Modal>
    );
};

export default observer(CreateDecryptedManualOrderModal);
